/**
 * GstFetchTrackerContext — app-level tracker for an in-flight GST notice
 * fetch so its completion result popup shows on ANY page (not just the GST
 * notices list).
 *
 * When a GST fetch is triggered, the page calls `startTracking(logId)`.
 * The provider persists the active log id to localStorage, polls
 * `/gst/fetch-status` globally every few seconds, and exposes `progress`
 * (incl. super-admin-only `fetchMethod` / `hasTiming`). On completion it
 * flips `showResult` true; the global <GstFetchResultPopup/> (rendered in
 * App) reads this context and shows the result regardless of route. Survives
 * navigation and reload (localStorage), so the user always sees the result.
 */
import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
} from "react";
import { postData } from "../api/apiService";
import { LAMBDA } from "../api/endpointMap";

const STORAGE_KEY = "gst_active_fetch_log";
// Poll faster while a fetch is in flight so the per-notice progress bar moves
// smoothly; the engine writes progress every time a row completes.
const POLL_MS = 2500;

const GstFetchTrackerContext = createContext(null);

export const GstFetchTrackerProvider = ({ children }) => {
  const [logId, setLogId] = useState(
    () => localStorage.getItem(STORAGE_KEY) || null,
  );
  const [progress, setProgress] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const timerRef = useRef(null);

  const stopPolling = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const startTracking = useCallback((newLogId) => {
    if (!newLogId) return;
    localStorage.setItem(STORAGE_KEY, newLogId);
    setLogId(newLogId);
    setShowResult(false);
    setProgress({ isComplete: false, status: "Processing" });
  }, []);

  const dismissResult = useCallback(() => {
    setShowResult(false);
    setProgress(null);
    setLogId(null);
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  // Poll the active fetch log while one is set.
  useEffect(() => {
    if (!logId) {
      stopPolling();
      return;
    }
    let cancelled = false;

    const poll = async () => {
      try {
        const resp = await postData(LAMBDA.GET_FETCH_STATUS_GST, {
          log_name: logId,
        });
        const result =
          resp?.data?.result?.result ||
          resp?.data?.result ||
          resp?.result ||
          {};
        if (cancelled) return;
        const p = {
          total: result.total_clients || 0,
          successful: result.successful_count || 0,
          failed: result.failed_count || 0,
          status: result.fetch_status || "Processing",
          isComplete: result.is_complete || false,
          fetchMethod: result.fetch_method || null, // super-admin only
          hasTiming: result.has_timing || false,
          // Live per-notice progress (all users): {phase, notices_total,
          // notices_done, files_done}. Null until the engine writes the first
          // update; the banner falls back to client counts in that window.
          notices: result.progress || null,
          logId,
        };
        setProgress(p);
        if (p.isComplete) {
          stopPolling();
          setShowResult(true);
        }
      } catch (e) {
        // transient — keep polling; don't crash the app shell
      }
    };

    poll();
    timerRef.current = setInterval(poll, POLL_MS);
    return () => {
      cancelled = true;
      stopPolling();
    };
  }, [logId, stopPolling]);

  return (
    <GstFetchTrackerContext.Provider
      value={{ logId, progress, showResult, startTracking, dismissResult }}
    >
      {children}
    </GstFetchTrackerContext.Provider>
  );
};

export const useGstFetchTracker = () => {
  const ctx = useContext(GstFetchTrackerContext);
  // Safe no-op fallback if used outside the provider (keeps callers simple).
  return (
    ctx || {
      logId: null,
      progress: null,
      showResult: false,
      startTracking: () => {},
      dismissResult: () => {},
    }
  );
};
